import React, { useState } from 'react';
import Button from '../../../components/ui/Button';
import Icon from '../../../components/AppIcon';
import { useTranslation } from 'react-i18next';

const HeroSection = () => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');

  const handleDemoRequest = () => {
    window.location.href = '/contact-sales';
  };

  const handleGetStarted = () => {
    window.location.href = '/developer-portal';
  };

  return (
    <section className="relative min-h-screen flex items-center justify-center bg-gradient-to-br from-background via-background to-card overflow-hidden">
      {/* Background Pattern */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute inset-0" style={{
          backgroundImage: `radial-gradient(circle at 1px 1px, rgba(59, 130, 246, 0.3) 1px, transparent 0)`,
          backgroundSize: '40px 40px'
        }}></div>
      </div>
      {/* Floating Elements */}
      <div className="absolute top-20 left-10 w-20 h-20 bg-primary/10 rounded-full blur-xl animate-pulse"></div>
      <div className="absolute bottom-20 right-10 w-32 h-32 bg-secondary/10 rounded-full blur-xl animate-pulse delay-1000"></div>
      <div className="absolute top-1/2 right-20 w-16 h-16 bg-accent/10 rounded-full blur-xl animate-pulse delay-500"></div>
      <div className="relative z-10 max-w-7xl mx-auto px-6 lg:px-8 text-center">
        <div className="max-w-4xl mx-auto">
          {/* Badge */}
          <div className="inline-flex items-center space-x-2 bg-primary/10 border border-primary/20 rounded-full px-4 py-2 mb-8">
            <Icon name="Zap" size={16} className="text-primary" />
            <span className="text-sm font-medium text-primary">
              {t('hero.uptime')}
            </span>
          </div>

          {/* Main Headline */}
          <h1 className="text-5xl lg:text-7xl font-bold text-foreground mb-6 leading-tight">
            {t('hero.headline')}
            <span className="block bg-gradient-to-r from-primary to-secondary bg-clip-text text-transparent">
              {t('hero.subheadline')}
            </span>
          </h1>

          {/* Subheadline */}
          <p className="text-xl lg:text-2xl text-muted-foreground mb-8 max-w-3xl mx-auto leading-relaxed">
            {t('hero.description')}
          </p>

          {/* Key Benefits */}
          <div className="flex flex-wrap justify-center items-center gap-6 mb-12 text-sm text-muted-foreground">
            <div className="flex items-center space-x-2">
              <Icon name="Shield" size={16} className="text-accent" />
              <span>{t('hero.benefits.security')}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Globe" size={16} className="text-accent" />
              <span>{t('hero.benefits.infrastructure')}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Code" size={16} className="text-accent" />
              <span>{t('hero.benefits.api')}</span>
            </div>
            <div className="flex items-center space-x-2">
              <Icon name="Clock" size={16} className="text-accent" />
              <span>{t('hero.benefits.setup')}</span>
            </div>
          </div>

          {/* CTA Buttons */}
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4 mb-12">
            <Button
              variant="default"
              size="lg"
              iconName="Calendar"
              iconPosition="left"
              onClick={handleDemoRequest}
              className="shadow-elevation-3 hover:shadow-elevation-4 bg-primary hover:bg-primary/90"
            >
              {t('hero.cta.demo')}
            </Button>
            <Button
              variant="outline"
              size="lg"
              iconName="ArrowRight"
              iconPosition="right"
              onClick={handleGetStarted}
              className="border-primary/30 hover:border-primary/50 hover:bg-primary/5"
            >
              {t('hero.cta.start')}
            </Button>
          </div>

          {/* Quick Start Email */}
          <div className="max-w-md mx-auto">
            <div className="flex flex-col sm:flex-row gap-3">
              <div className="flex-1">
                <input
                  type="email"
                  placeholder={t('hero.email_placeholder')}
                  value={email}
                  onChange={(e) => setEmail(e?.target?.value)}
                  className="w-full px-4 py-3 bg-input border border-border rounded-lg text-foreground placeholder-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
                />
              </div>
              <Button
                variant="secondary"
                size="default"
                iconName="Send"
                iconPosition="right"
                className="whitespace-nowrap"
              >
                {t('hero.get_api_key')}
              </Button>
            </div>
            <p className="text-xs text-muted-foreground mt-2">
              {t('hero.free_tier')}
            </p>
          </div>
        </div>

        {/* Trust Indicators */}
        <div className="mt-20">
          <p className="text-sm text-muted-foreground mb-8">
            {t('hero.trusted_by')}
          </p>
          <div className="flex flex-wrap justify-center items-center gap-8 opacity-60">
            <div className="text-2xl font-bold text-muted-foreground">TechCorp</div>
            <div className="text-2xl font-bold text-muted-foreground">StartupXYZ</div>
            <div className="text-2xl font-bold text-muted-foreground">Enterprise Inc</div>
            <div className="text-2xl font-bold text-muted-foreground">DevTools Co</div>
            <div className="text-2xl font-bold text-muted-foreground">CloudSoft</div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;